// src/draft/domain/services/draftService.ts

import type {
  AnyDraftPlayer,
  DraftGoalkeeper,
  DraftPlayer,
  DraftPlayerRole,
} from "~~/src/draft/domain/models/DraftPlayer";
import type { MacroFilters } from "~~/src/draft/domain/models/Filters";
import type { PlayerDTO } from "~~/src/player/infrastrructure/DTO/PlayerDTO";
import type {
  ReserveGoalkeeper,
  ReserveGoalkeeperMacros,
  ReservePlayer,
  ReservePlayerMacros,
} from "~~/src/draft/domain/models/ReservePlayers";

interface DraftService {
  createMinMaxMacrosFilters: (players: AnyDraftPlayer[]) => MacroFilters | undefined;
  findMacroMinMax: (macroKey: string, players: AnyDraftPlayer[]) => [number, number];
  findHeightMinMax: (players: AnyDraftPlayer[]) => [number, number];
  normalizeDraftReservePlayer: (player: PlayerDTO) => ReserveGoalkeeper | ReservePlayer;
  normalizeDraftPlayer: (player: PlayerDTO) => DraftGoalkeeper | DraftPlayer;
}

const createMinMaxMacrosFilters = (players: AnyDraftPlayer[]): MacroFilters | undefined => {
  let filters = {} as MacroFilters;
  const [firstPlayer] = players;
  if (firstPlayer) {
    for (const macrosKey in firstPlayer.macros) {
      const [min, max] = findMacroMinMax(macrosKey, players);
      filters[macrosKey] = { min, max };
    }
    return filters;
  }

  return undefined;
};

const findMacroMinMax = (macroKey: string, players: AnyDraftPlayer[]): [number, number] => {
  const valuesArr: number[] = players.map(p => p.macros[macroKey]);
  return findMinMax(valuesArr);
};

const findHeightMinMax = (players: AnyDraftPlayer[]): [number, number] => {
  const valuesArr: number[] = players.map(p => p.height);
  const valuesWithValue = valuesArr.filter(v => !!v);
  return findMinMax(valuesWithValue);
};

const normalizeDraftReservePlayer = (player: PlayerDTO): ReservePlayer | ReserveGoalkeeper => {
  return {
    id: player.id,
    draftOrigin: player.metaInformation?.draftOrigin
      ? Number(player.metaInformation?.draftOrigin)
      : undefined,
    draftJersey: player.metaInformation?.draftJersey ? player.metaInformation?.draftJersey : undefined,
    role: player.role ? setDraftRole(player.role) : undefined,
    name: player.shortName,
    foot: player.foot,
    height: player.height || 100,
    birthdate: transformPlayerBirthDate(player.birthDate),
    country: player.countryId,
    macros: setPlayerMacros(player),
    matchYtVideoId: player.metaInformation?.matchLink
      ? getYtVideoIdFromUrl(player.metaInformation?.matchLink)
      : undefined,
    profileYtVideoId: player.metaInformation?.videoLink
      ? getYtVideoIdFromUrl(player.metaInformation?.videoLink)
      : undefined,
    imageUrl: player.image?.url,
  };
};

const normalizeDraftPlayer = (player: PlayerDTO): DraftPlayer | DraftGoalkeeper => {
  return {
    id: player.id,
    draftJersey: player.metaInformation?.jerseyNumber ? player.metaInformation?.jerseyNumber : undefined,
    role: player.role ? setDraftRole(player.role) : undefined,
    name: player.shortName,
    foot: player.foot,
    height: player.height || 100,
    birthdate: transformPlayerBirthDate(player.birthDate),
    country: player.countryId,
    macros: setPlayerMacros(player),
    matchYtVideoId: player.metaInformation?.matchLink
      ? getYtVideoIdFromUrl(player.metaInformation?.matchLink)
      : undefined,
    profileYtVideoId: player.metaInformation?.videoLink
      ? getYtVideoIdFromUrl(player.metaInformation?.videoLink)
      : undefined,
    imageUrl: player.image?.url,
  };
};

const draftService = (): DraftService => ({
  createMinMaxMacrosFilters,
  findMacroMinMax,
  findHeightMinMax,
  normalizeDraftReservePlayer,
  normalizeDraftPlayer,
});

export default draftService;

function findMinMax(values: number[]): [number, number] {
  return [Math.min(...values), Math.max(...values)];
}

function setDraftRole(role: string | null): DraftPlayerRole | undefined {
  switch (role) {
    case "forward":
      return "DL";
    case "midfielder":
      return "MC";
    case "defender":
      return "DF";
    case "goalkeeper":
      return "PT";
    default:
      return undefined;
  }
}

function getYtVideoIdFromUrl(url: string): string | undefined {
  if (!!url) {
    if (url.includes("youtu.be")) {
      const splitUrl = url.split("/");
      return splitUrl[splitUrl.length - 1];
    } else if (url.includes("watch?")) {
      const splitUrl = url.split("=");
      return splitUrl[splitUrl.length - 1];
    }
  } else {
    return undefined;
  }
}

function setPlayerMacros(player: PlayerDTO): ReservePlayerMacros | ReserveGoalkeeperMacros {
  if (!player.role) {
    return {
      physical: 0,
      duels: 0,
      shooting: 0,
      defence: 0,
      passing: 0,
      skills: 0,
      average: 0,
    };
  } else if (player.role === "goalkeeper") {
    return {
      duels: player.metaInformation?.duels ? Number(player.metaInformation.duels) : 0,
      passing: player.metaInformation?.goalkeeperPassing
        ? Number(player.metaInformation.goalkeeperPassing)
        : 0,
      diving: player.metaInformation?.diving ? Number(player.metaInformation.diving) : 0,
      anticipation: player.metaInformation?.anticipation ? Number(player.metaInformation.anticipation) : 0,
      handling: player.metaInformation?.handling ? Number(player.metaInformation.handling) : 0,
      reflexes: player.metaInformation?.reflexes ? Number(player.metaInformation.reflexes) : 0,
      average: player.metaInformation?.average ? Number(player.metaInformation.average) : 0,
    };
  } else {
    return {
      physical: player.metaInformation?.physical ? Number(player.metaInformation.physical) : 0,
      duels: player.metaInformation?.duels ? Number(player.metaInformation.duels) : 0,
      shooting: player.metaInformation?.shooting ? Number(player.metaInformation.shooting) : 0,
      defence: player.metaInformation?.defence ? Number(player.metaInformation.defence) : 0,
      passing: player.metaInformation?.passing ? Number(player.metaInformation.passing) : 0,
      skills: player.metaInformation?.skills ? Number(player.metaInformation.skills) : 0,
      average: player.metaInformation?.average ? Number(player.metaInformation.average) : 0,
    };
  }
}

function transformPlayerBirthDate(date: string): string {
  if (!date) return "-";
  const splitDateByScore = date.split("-");
  return splitDateByScore.reverse().join("/");
}
